import { type GenericError, type HttpTimeoutError } from '@cian/peperrors';
import { useCallback, useMemo, useState } from 'react';

import { ASYNC_STATUS, type AsyncStatus } from './types';
import { type withTypeSafeApiResult } from './withTypeSafeApiResult';
import { ResponseError } from '../../errors';

type UseTypeSafeApiResultIdle = {
  data: undefined;
  error: undefined;
  status: typeof ASYNC_STATUS.IDLE;
};

type UseTypeSafeApiResultPending = {
  data: undefined;
  error: undefined;
  status: typeof ASYNC_STATUS.PENDING;
};

type UseTypeSafeApiResultSuccess<Response> = {
  data: Response;
  error: undefined;
  status: typeof ASYNC_STATUS.SUCCESS;
};

type UseTypeSafeApiResultError = {
  data: undefined;
  error: GenericError | HttpTimeoutError | ResponseError;
  status: typeof ASYNC_STATUS.ERROR;
};

type UseTypeSafeApiResult<Response, Request> = (
  | UseTypeSafeApiResultIdle
  | UseTypeSafeApiResultPending
  | UseTypeSafeApiResultSuccess<Response>
  | UseTypeSafeApiResultError
) & {
  safeFetch: ReturnType<typeof withTypeSafeApiResult<Response, Request>>;
};

/* istanbul ignore next */
export function useTypeSafeApiResult<Response, Request>(
  fetchFunctionSafe: ReturnType<typeof withTypeSafeApiResult<Response, Request>>,
  options: { skipCancelErrors?: boolean } = { skipCancelErrors: true },
): UseTypeSafeApiResult<Response, Request> {
  const [data, setData] = useState<Response | undefined>(undefined);
  const [error, setError] = useState<GenericError | HttpTimeoutError | ResponseError | undefined>(undefined);
  const [status, setStatus] = useState<AsyncStatus>('idle');

  const handleFetch = useCallback(
    async (...params: Parameters<typeof fetchFunctionSafe>) => {
      setError(undefined);
      setData(undefined);
      setStatus('pending');

      const result = await fetchFunctionSafe(...params);

      result
        .map(successData => {
          setData(successData);
          setStatus(ASYNC_STATUS.SUCCESS);
        })
        .unwrapOrElse(err => {
          // Если предыдущий запрос здесь был отменён, то значит он был запрошен повторно.
          // Следовательно, не нужно скрывать спиннер, а продолжать процесс загрузки.
          // Если этого не делать, то будет рассинхрон состояния.
          // На данный момент нельзя отловить через metaResult.isErrAnd(HttpCancelError.is),
          // поскольку прилетает UnknownError если запрос был отменён через cancellationToken.cancel();
          if (!options.skipCancelErrors || !err.message.toLowerCase().includes('canceled')) {
            setError(err);
            setStatus(ASYNC_STATUS.ERROR);
          }
        });

      return result;
    },
    [fetchFunctionSafe, options.skipCancelErrors],
  );

  return useMemo(
    () => ({ data, error, status, safeFetch: handleFetch }) as UseTypeSafeApiResult<Response, Request>,
    [data, error, handleFetch, status],
  );
}
